import React from 'react';
import {
  BarChart as RechartsBar,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

/**
 * Asset Exposure chart component - vertical bar chart showing assets by max risk
 * @param {Object} props
 * @param {Array} props.data - Array of {asset_name, max_risk}
 */
export default function AssetExposureChart({ data = [] }) {
  // Color coding based on risk level (1-25 scale for risk_x_asset)
  const getRiskColor = (risk) => {
    if (risk >= 15) return '#dc2626'; // Critical - red
    if (risk >= 10) return '#fb923c'; // High - orange
    if (risk >= 5) return '#facc15'; // Moderate - yellow
    if (risk >= 3) return '#38bdf8'; // Low - sky blue
    return '#94a3b8'; // Info - slate
  };

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500 text-sm">
        No asset data available
      </div>
    );
  }

  // Prepare data for recharts (limit asset names to reasonable length)
  const chartData = data.map((asset) => {
    const risk = asset.max_risk || 0;
    return {
      name: asset.asset_name.length > 20 ? asset.asset_name.slice(0, 17) + '...' : asset.asset_name,
      fullName: asset.asset_name,
      value: risk,
      fill: getRiskColor(risk),
    };
  });

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={280}>
        <RechartsBar
          data={chartData}
          margin={{ top: 8, right: 16, bottom: 40, left: 8 }}
          barCategoryGap="20%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={80}
            tick={{ fontSize: 10, fill: '#94a3b8' }}
            axisLine={{ stroke: '#e2e8f0' }}
            tickLine={false}
            interval={0}
          />
          <YAxis
            domain={[0, 'dataMax']}
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            formatter={(value) => [value.toFixed(1), 'Risk Score']}
            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
            contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.07)' }}
          />
          <Bar
            dataKey="value"
            name="Risk Score"
            radius={[6, 6, 0, 0]}
            barSize={18}
            fill="#94a3b8"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </RechartsBar>
      </ResponsiveContainer>
    </div>
  );
}
